import { Environment } from '../../utils/Environment';

import { AnalyticsEvents } from './AnalyticsEvents';
import { DeviceInfo, getDeviceInfo } from './getDeviceInfo';

const sdkType = 'WEB';
const sdkVersion = Environment.get<string>('PRIMER_SDK_VERSION');

export interface AnalyticsEvent<T> {
  checkoutSessionId: string;
  clientSessionId?: string;
  createdAt: number;
  customerId?: string;
  device?: DeviceInfo;
  eventType: AnalyticsEvents;
  /**
   * Hostname of the page running the SDK (e.g. `merchant.example.com`).
   * Always present — populated from `window.location.hostname` at event
   * creation. Useful for identifying the calling merchant before the
   * client configuration loads (which provides `primerAccountId` and
   * `clientSessionId`).
   */
  hostname?: string;
  /**
   * Full origin of the page running the SDK (protocol + host + port).
   * Always present unless running outside a browser.
   */
  origin?: string;
  primerAccountId?: string;
  properties: T;
  sdkType: string;
  sdkVersion: string;
}

export type AnalyticsOptions = Pick<
  AnalyticsEvent<unknown>,
  'checkoutSessionId' | 'clientSessionId' | 'customerId' | 'primerAccountId'
> & {
  url?: string;
};

const getBrowserContext = () => {
  if (typeof window === 'undefined' || !window.location) {
    return {};
  }

  return {
    hostname: window.location.hostname,
    origin: window.location.origin,
  };
};

export async function createAnalyticsEvent<T>(
  eventType: AnalyticsEvents,
  properties: T,
  options: AnalyticsOptions,
): Promise<AnalyticsEvent<T>> {
  return {
    ...options,
    ...getBrowserContext(),
    createdAt: Date.now(),
    device: await getDeviceInfo(),
    eventType,
    properties,
    sdkVersion,
    sdkType,
  };
}
